import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CheckCircle2,
  ImagePlus,
  Link2,
  MonitorPlay,
  Trash2,
  Upload,
  Video,
} from 'lucide-react';
import StockManagerLayout from '../components/StockManagerLayout';
import {
  createAdvertisement,
  deleteAdvertisement,
  fetchAdvertisements,
  updateAdvertisement,
  uploadAdvertisementMedia,
} from '../features/marketing/advertisementSlice';
import { fetchAllProducts } from '../features/products/productSlice';
import { fetchCatalogEntity } from '../features/inventory/catalogCrudSlice';

const templates = [
  {
    id: 'clearance_sale',
    name: 'Clearance Sale',
    saleType: 'clearance',
    className: 'bg-gradient-to-br from-red-800 via-red-600 to-amber-400 text-white',
    areas: ['hero', 'left', 'right'],
    productsPerSlide: 2,
  },
  {
    id: 'dasara_template',
    name: 'Dasara Festival',
    saleType: 'festival',
    className: 'bg-gradient-to-br from-yellow-300 via-orange-500 to-gray-900 text-white',
    areas: ['hero', 'offer', 'brand'],
    productsPerSlide: 4,
  },
  {
    id: 'brand_spotlight',
    name: 'Brand Spotlight',
    saleType: 'brand',
    className: 'bg-gradient-to-br from-emerald-800 via-green-600 to-yellow-300 text-white',
    areas: ['hero', 'brand', 'product'],
    productsPerSlide: 6,
  },
  {
    id: 'price_blast',
    name: 'Price Blast',
    saleType: 'offer',
    className: 'bg-gradient-to-br from-fuchsia-800 via-rose-600 to-orange-300 text-white',
    areas: ['hero', 'price', 'product'],
    productsPerSlide: 2,
  },
  {
    id: 'fresh_market',
    name: 'Fresh Market',
    saleType: 'fresh',
    className: 'bg-gradient-to-br from-lime-700 via-emerald-500 to-cyan-300 text-white',
    areas: ['hero', 'fresh', 'product'],
    productsPerSlide: 4,
  },
  {
    id: 'premium_gold',
    name: 'Premium Gold',
    saleType: 'premium',
    className: 'bg-gradient-to-br from-zinc-950 via-stone-800 to-yellow-500 text-white',
    areas: ['hero', 'brand', 'product'],
    productsPerSlide: 6,
  },
];

const emptySlide = (sequence = 1) => ({
  title: '',
  description: '',
  product_name: '',
  brand_name: '',
  media_type: 'image',
  media_path: '',
  preview_url: '',
  target_area: 'hero',
  sequence,
  duration_seconds: 6,
  repository_id: '',
  financial_search: '',
  selected_financial: null,
  source_metadata: {},
});

const defaultForm = {
  name: '',
  outlet_id: '',
  template: 'clearance_sale',
  sale_type: 'clearance',
  type: 'html',
  priority: 1,
  sequence: 1,
  timer_seconds: 10,
  start_date: '',
  end_date: '',
  status: 'draft',
  generated_video_path: '',
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const getUsableProductImage = (value) => {
  const imageUrl = String(value || '').trim();
  if (!imageUrl) return '';

  try {
    const url = new URL(imageUrl);
    const isPrivateLegacyBucketUrl =
      url.hostname === 'storage.googleapis.com' &&
      url.pathname.toLowerCase().startsWith('/manakirana-988b3.appspot.com/');

    return isPrivateLegacyBucketUrl ? '' : imageUrl;
  } catch {
    return imageUrl;
  }
};

const getProductBarcodeId = (financial = {}) =>
  [
    financial.product_barcode_id,
    financial.productBarcodeId,
    financial.catalogProductBarcodeId,
    financial.product_barcode_id_fk,
    financial.barcode_id,
    financial.catalog_barcode_id,
    financial.mkid,
  ]
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .map((value) => String(value ?? '').trim())
    .find(Boolean) || '';

const money = (value) => `Rs. ${Number(value || 0).toFixed(2)}`;

const firstPackValue = (...values) =>
  values.find((value) => value !== null && value !== undefined && String(value).trim() !== '') || '';

const getUnitFromMkBarcode = (value) => {
  const barcode = String(value || '');
  if (barcode.length < 15) return '';
  const unitCode = barcode.slice(12, 14);
  if (unitCode === '02') return 'GMS';
  if (unitCode === '03') return 'KGS';
  if (unitCode === '01') return 'QTY';
  return '';
};

const getPackDetails = (financial = {}, detail = {}, product = {}) => {
  const mkBarcode = String(
    firstPackValue(financial.mk_barcode, financial.mkBarcode, financial.barcode)
  );
  const encodedQuantity = mkBarcode.length >= 17 ? Number(mkBarcode.slice(14, 17)) : 0;
  const quantity =
    (Number.isFinite(encodedQuantity) && encodedQuantity > 0 ? encodedQuantity : '') ||
    firstPackValue(
      financial.quantity,
      financial.barcode_quantity,
      financial.packQuantity,
      financial.qty,
      detail.quantity,
      product.catalogQuantity,
      product.quantity
    );
  const units =
    getUnitFromMkBarcode(mkBarcode) ||
    firstPackValue(
      financial.units,
      financial.unit_short_code,
      financial.unit_name,
      financial.unit,
      detail.units,
      detail.unit_short_code,
      product.units,
      product.unit_short_code,
      product.unit_name
    );
  const weight = firstPackValue(financial.weight, detail.weight, product.weight);
  const quantityText = [quantity, units].filter(Boolean).join(' ').trim() || String(weight || '').trim();

  return { quantity, units, quantityText };
};

const getOutletId = (outlet = {}) => String(outlet.id || outlet._id || outlet.outlet_id || '');
const getOutletLabel = (outlet = {}) =>
  [outlet.outlet_code, outlet.outlet_name || outlet.name].filter(Boolean).join(' - ') ||
  getOutletId(outlet);

const toLocalDateInput = (date = new Date()) => {
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const isAdvertisementExpired = (item = {}) => {
  if (!item.end_date) return false;
  const endDate = String(item.end_date).slice(0, 10);
  return endDate < toLocalDateInput();
};

const getAdvertisementConfig = (item = {}) => {
  if (item.config && typeof item.config === 'object') return item.config;
  if (typeof item.config !== 'string') return {};
  try {
    return JSON.parse(item.config);
  } catch {
    return {};
  }
};

const getDetailMetadata = (detail = {}) => {
  if (detail.metadata && typeof detail.metadata === 'object') return detail.metadata;
  if (typeof detail.metadata !== 'string') return {};
  try {
    return JSON.parse(detail.metadata);
  } catch {
    return {};
  }
};

const flattenFinancials = (products) =>
  asArray(products).flatMap((product) =>
    asArray(product?.details).flatMap((detail) =>
      asArray(detail?.financials).map((financial) => {
        const pack = getPackDetails(financial, detail, product);

        const mrp = Number(financial?.price || 0);
        const salePrice = Number(financial?.dprice || financial?.price || 0);
        const computedDiscount =
          mrp > 0 && salePrice > 0 && salePrice < mrp
            ? Math.round(((mrp - salePrice) / mrp) * 100)
            : 0;
        const enteredDiscount = Number(financial?.Discount || financial?.discount || 0);

        return {
          key: [product?._id, detail?._id, financial?._id].filter(Boolean).join(':'),
          productId: product?._id || '',
          brandId: detail?._id || '',
          financialId: financial?._id || '',
          productName: product?.name || '',
          category: product?.category || '',
          brandName: detail?.brand || '',
          quantity: pack.quantity,
          units: pack.units,
          quantityText: pack.quantityText,
          mrp,
          salePrice,
          discountPercent: Math.max(computedDiscount, enteredDiscount),
          discountAmount: Math.max(mrp - salePrice, 0),
          stock: Number(financial?.countInStock || 0),
          barcode: getProductBarcodeId(financial),
          mkBarcode: financial?.mk_barcode || financial?.mkBarcode || '',
          image: getUsableProductImage(detail?.images?.[0]?.image),
        };
      })
    )
  );

const getFinancialLabel = (item) =>
  [
    item.productName,
    item.brandName,
    item.quantityText,
    item.salePrice ? money(item.salePrice) : '',
    item.barcode ? `ID ${item.barcode}` : '',
  ]
    .filter(Boolean)
    .join(' | ');

const AdvertisementsPage = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.posUser?.userInfo?.token);
  const products = useSelector((state) => state.products?.all || []);
  const outlets = useSelector((state) => state.catalogCrud?.data?.outlets || []);
  const { items, loading, saving, error, successMessage } = useSelector(
    (state) => state.advertisements || {}
  );
  const [form, setForm] = useState(defaultForm);
  const [slides, setSlides] = useState([emptySlide(1)]);
  const [editingAdId, setEditingAdId] = useState(null);
  const [topProductCount, setTopProductCount] = useState(5);
  const [minimumDiscount, setMinimumDiscount] = useState(0);
  const [productSearch, setProductSearch] = useState('');
  const [tvScreenUrl, setTvScreenUrl] = useState('');
  const [activeEditor, setActiveEditor] = useState('product-advertisements');
  const hasTvVideo = Boolean(tvScreenUrl.trim());
  const hasSelectedProducts = slides.some((slide) => slide.selected_financial || slide.product_name);

  const activeTemplate = useMemo(
    () => templates.find((template) => template.id === form.template) || templates[0],
    [form.template]
  );
  const financialOptions = useMemo(() => flattenFinancials(products), [products]);
  const highDiscountProducts = useMemo(
    () =>
      financialOptions
        .filter(
          (item) =>
            item.mrp > 0 &&
            item.salePrice > 0 &&
            item.salePrice < item.mrp &&
            item.discountPercent >= Math.max(0, Number(minimumDiscount) || 0)
        )
        .sort((a, b) => {
          if (b.discountPercent !== a.discountPercent) {
            return b.discountPercent - a.discountPercent;
          }
          if (b.discountAmount !== a.discountAmount) {
            return b.discountAmount - a.discountAmount;
          }
          return b.stock - a.stock;
        }),
    [financialOptions, minimumDiscount]
  );
  const displayedProducts = useMemo(() => {
    const query = String(productSearch || '').trim().toLowerCase();
    if (!query) return highDiscountProducts;

    return financialOptions
      .filter((item) =>
        [
          item.productName,
          item.brandName,
          item.quantityText,
          item.barcode,
          item.category,
          item.salePrice,
          item.mrp,
        ]
          .join(' ')
          .toLowerCase()
          .includes(query)
      )
      .sort((a, b) => {
        if (b.discountPercent !== a.discountPercent) {
          return b.discountPercent - a.discountPercent;
        }
        return b.stock - a.stock;
      });
  }, [financialOptions, highDiscountProducts, productSearch]);
  const featuredSideProducts = highDiscountProducts.slice(0, 2);

  useEffect(() => {
    dispatch(fetchAdvertisements());
    dispatch(fetchCatalogEntity('outlets'));
  }, [dispatch]);

  useEffect(() => {
    if (token) {
      dispatch(fetchAllProducts({ token, localFirst: true }));
    }
  }, [dispatch, token]);

  const updateForm = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const buildAdvertisementPayload = (nextStatus = form.status) => {
    if (!form.name.trim()) {
      return null;
    }

    const details = slides
      .filter((slide) => slide.media_path || slide.title || slide.product_name)
      .map(({ preview_url, financial_search, selected_financial, source_metadata, ...slide }) => ({
        ...slide,
        repository_id: slide.repository_id || null,
        sequence: Number(slide.sequence || 1),
        duration_seconds: Number(slide.duration_seconds || 5),
        metadata: {
          ...(source_metadata || {}),
          template: form.template,
          target_area: slide.target_area,
          mongo_product_id: selected_financial?.productId || source_metadata?.mongo_product_id || null,
          mongo_brand_id: selected_financial?.brandId || source_metadata?.mongo_brand_id || null,
          mongo_financial_id: selected_financial?.financialId || source_metadata?.mongo_financial_id || null,
          quantity: selected_financial?.quantity || source_metadata?.quantity || null,
          units: selected_financial?.units || source_metadata?.units || null,
          mrp: selected_financial?.mrp || source_metadata?.mrp || null,
          sale_price: selected_financial?.salePrice || source_metadata?.sale_price || null,
          barcode: selected_financial?.barcode || source_metadata?.barcode || null,
          mk_barcode: selected_financial?.mkBarcode || source_metadata?.mk_barcode || null,
        },
      }));

    const advertisement = {
      ...form,
      status: nextStatus,
      outlet_id: form.outlet_id || null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      priority: Number(form.priority || 1),
      sequence: Number(form.sequence || 1),
      timer_seconds: Number(form.timer_seconds || 10),
      generated_video_path:
        form.generated_video_path ||
        `/advertisements/${form.template}-${Date.now()}.mp4`,
      config: {
        template: form.template,
        areas: activeTemplate.areas,
        products_per_slide: activeTemplate.productsPerSlide,
        tv_screen_url: tvScreenUrl.trim() || null,
        slides: details,
      },
    };

    return { advertisement, details };
  };

  const resetAdvertisementForm = () => {
    setForm(defaultForm);
    setSlides([emptySlide(1)]);
    setTvScreenUrl('');
    setEditingAdId(null);
  };

  const saveAdvertisement = async (nextStatus = form.status) => {
    const payload = buildAdvertisementPayload(nextStatus);

    if (!payload) {
      alert('Enter advertisement name.');
      return;
    }

    if (editingAdId) {
      await dispatch(
        updateAdvertisement({
          id: editingAdId,
          payload: {
            ...payload.advertisement,
            details: payload.details,
          },
        })
      );
    } else {
      await dispatch(createAdvertisement(payload));
    }

    resetAdvertisementForm();
  };

  const handleTvScreenFile = async (file) => {
    if (!file) return;
    try {
      const uploaded = await dispatch(uploadAdvertisementMedia(file)).unwrap();
      setTvScreenUrl(uploaded.media_path);
    } catch (uploadError) {
      alert(`Upload failed: ${uploadError}`);
    }
  };

  const editAdvertisement = (item) => {
    const itemTemplate =
      templates.find((template) => template.id === item.template) || templates[0];
    const itemConfig = getAdvertisementConfig(item);
    setEditingAdId(item.id);
    setForm({
      name: item.name || '',
      outlet_id: item.outlet_id || '',
      template: item.template || 'clearance_sale',
      sale_type: item.sale_type || '',
      type: item.type || 'html',
      priority: item.priority || 1,
      sequence: item.sequence || 1,
      timer_seconds: item.timer_seconds || 10,
      start_date: item.start_date ? String(item.start_date).slice(0, 10) : '',
      end_date: item.end_date ? String(item.end_date).slice(0, 10) : '',
      status: item.status || 'draft',
      generated_video_path: item.generated_video_path || '',
    });
    setTvScreenUrl(itemConfig.tv_screen_url || itemConfig.tvUrl || itemConfig.screen_url || '');
    setSlides(
      (item.details?.length ? item.details : [emptySlide(1)]).map((detail, index) => ({
        title: detail.title || '',
        description: detail.description || '',
        product_name: detail.product_name || '',
        brand_name: detail.brand_name || '',
        media_type: detail.media_type || 'image',
        media_path: detail.media_path || '',
        preview_url: '',
        target_area: detail.target_area || itemTemplate.areas[index % itemTemplate.areas.length],
        sequence: detail.sequence || index + 1,
        duration_seconds: detail.duration_seconds || 6,
        repository_id: detail.repository_id || '',
        financial_search: detail.product_name || '',
        selected_financial: null,
        source_metadata: getDetailMetadata(detail),
      }))
    );
    setActiveEditor('center-video');
    setTimeout(() => {
      document.getElementById('advertisement-editors')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const toggleAdvertisementStatus = async (item) => {
    const expired = isAdvertisementExpired(item);
    const isLive = item.status === 'active' && !expired;
    const payload = isLive
      ? { status: 'draft' }
      : {
          status: 'active',
          ...(expired
            ? { start_date: toLocalDateInput(), end_date: null }
            : {}),
        };

    if (expired && !window.confirm(
      'This campaign has expired. Republish it from today with no end date?'
    )) return;

    await dispatch(updateAdvertisement({ id: item.id, payload }));
  };

  const makeSlideFromFinancial = (financial, sequence) => {
    const priceText =
      financial.salePrice && financial.mrp && financial.salePrice < financial.mrp
        ? `${money(financial.salePrice)} instead of ${money(financial.mrp)}`
        : money(financial.salePrice || financial.mrp);

    return {
      ...emptySlide(sequence),
      title: financial.productName,
      description: priceText,
      product_name: financial.productName,
      brand_name: financial.brandName,
      media_type: financial.image ? 'image' : 'image',
      media_path: financial.image,
      target_area: activeTemplate.areas[(sequence - 1) % activeTemplate.areas.length],
      financial_search: getFinancialLabel(financial),
      selected_financial: financial,
    };
  };

  const addFinancialAsSlide = (financial) => {
    setSlides((prev) => [...prev, makeSlideFromFinancial(financial, prev.length + 1)]);
  };

  const addTopDiscountSlides = () => {
    const requestedCount = Math.min(100, Math.max(1, Number(topProductCount) || 1));
    const nextProducts = highDiscountProducts.slice(0, requestedCount);
    if (!nextProducts.length) return;
    setSlides(nextProducts.map((financial, index) => makeSlideFromFinancial(financial, index + 1)));
    setForm((prev) => ({
      ...prev,
      name: prev.name || 'High Discount Offers',
      sale_type: prev.sale_type || 'clearance',
    }));
  };

  const useFeaturedSideOffers = () => {
    if (!featuredSideProducts.length) return;
    setSlides((prev) => {
      const featuredKeys = new Set(featuredSideProducts.map((item) => item.key));
      const remaining = prev.filter((slide) => !featuredKeys.has(slide.selected_financial?.key));
      return [
        ...featuredSideProducts.map((financial, index) => makeSlideFromFinancial(financial, index + 1)),
        ...remaining.map((slide, index) => ({ ...slide, sequence: featuredSideProducts.length + index + 1 })),
      ];
    });
    setForm((prev) => ({ ...prev, name: prev.name || 'Hadavidi Featured Offers' }));
  };

  const openEditorSection = (sectionId) => {
    setActiveEditor(sectionId);
    requestAnimationFrame(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <StockManagerLayout>
      <main className="space-y-4">
        <section className="overflow-hidden rounded-2xl bg-gradient-to-r from-slate-950 via-blue-950 to-blue-700 p-4 text-white shadow-lg sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-200">Hadavidi content manager</p>
              <h1 className="mt-1 text-2xl font-black sm:text-3xl">Create and publish advertisements</h1>
              <p className="mt-1 max-w-2xl text-sm text-blue-100">
                Choose products, add an optional video, preview the campaign, and publish it to every connected TV.
              </p>
            </div>
            {!editingAdId ? (
              <button
                type="button"
                onClick={() => saveAdvertisement('active')}
                disabled={saving}
                className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-amber-400 px-5 text-sm font-black text-slate-950 shadow-md hover:bg-amber-300 disabled:bg-gray-400 lg:w-auto"
              >
                <MonitorPlay size={16} />
                Publish to Hadavidi
              </button>
            ) : null}
          </div>
          <div id="advertisement-editors" className="mt-5 scroll-mt-4 grid gap-3 sm:grid-cols-3">
            {[
              ['1', 'Product advertisements', 'Build the moving product offer rows', 'product-advertisements'],
              ['2', 'Center video', 'Paste YouTube URL or upload a video', 'center-video'],
              ['3', 'Two featured products', 'Choose the offers beside the video', 'featured-products'],
            ].map(([number, title, description, sectionId]) => (
              <button
                key={number}
                type="button"
                onClick={() => openEditorSection(sectionId)}
                className={`flex items-center gap-3 rounded-xl border p-3 text-left transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-amber-300 ${
                  activeEditor === sectionId
                    ? 'border-amber-300 bg-white/25 ring-2 ring-amber-300'
                    : 'border-white/20 bg-white/10 hover:bg-white/20'
                }`}
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white font-black text-blue-950">{number}</span>
                <span className="min-w-0">
                  <strong className="block text-sm">{title}</strong>
                  <small className="block text-blue-100">{description}</small>
                </span>
              </button>
            ))}
          </div>
        </section>

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            {error}
          </div>
        ) : null}
        {successMessage ? (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-700">
            {successMessage}
          </div>
        ) : null}
        {editingAdId ? (
          <section className="flex flex-col gap-3 rounded-2xl border-2 border-amber-300 bg-amber-50 px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.16em] text-amber-700">Campaign editor</p>
              <h2 className="mt-1 text-xl font-black text-gray-950 sm:text-2xl">
                Editing campaign: {form.name || 'Untitled campaign'}
              </h2>
              <p className="mt-1 text-sm text-gray-600">Update products, center video, featured offers, and campaign details before publishing.</p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <button
                type="button"
                onClick={() => saveAdvertisement('active')}
                disabled={saving}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-blue-700 px-5 text-sm font-black text-white hover:bg-blue-800 disabled:bg-gray-400"
              >
                <MonitorPlay size={16} />
                Save & Publish
              </button>
              <button
                type="button"
                onClick={resetAdvertisementForm}
                className="h-11 rounded-lg border border-amber-300 bg-white px-4 text-sm font-bold text-gray-700 hover:bg-amber-100"
              >
                Cancel editing
              </button>
            </div>
          </section>
        ) : null}
        <section className="grid min-w-0 gap-4 xl:grid-cols-[minmax(300px,360px)_minmax(0,1fr)]">
          <div className="space-y-4">
            {editingAdId || activeEditor === 'product-advertisements' ? (
            <div id="product-advertisements" className="scroll-mt-4 rounded-2xl border border-blue-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3">
                <div>
                  <span className="text-xs font-black uppercase tracking-wide text-blue-700">1 · Product advertisements</span>
                  <h2 className="mt-1 font-bold text-gray-900">Products for the moving offer rows</h2>
                  <p className="text-xs text-gray-500">Search products or automatically add the strongest discounts.</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="text-sm font-bold text-gray-700">
                    Number of products
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={topProductCount}
                      onChange={(event) => setTopProductCount(event.target.value)}
                      className="mt-1 block h-11 w-full rounded-lg border px-3 text-base text-gray-900"
                    />
                  </label>
                  <label className="text-sm font-bold text-gray-700">
                    Minimum discount %
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={minimumDiscount}
                      onChange={(event) => setMinimumDiscount(event.target.value)}
                      className="mt-1 block h-11 w-full rounded-lg border px-3 text-base text-gray-900"
                    />
                  </label>
                </div>
              </div>
              <button
                type="button"
                onClick={addTopDiscountSlides}
                disabled={!highDiscountProducts.length}
                className="mt-3 h-9 w-full rounded-lg bg-blue-700 text-sm font-semibold text-white disabled:bg-gray-300"
              >
                Get Top {Math.min(100, Math.max(1, Number(topProductCount) || 1))} Discount Products
              </button>
              <input
                value={productSearch}
                onChange={(event) => setProductSearch(event.target.value)}
                className="mt-3 h-10 w-full rounded-lg border px-3 text-sm"
                placeholder="Search products by name, brand, barcode"
              />
              <p className="mt-2 text-xs font-semibold text-gray-500">
                {displayedProducts.length} matching product{displayedProducts.length === 1 ? '' : 's'}
              </p>
              <div
                className="mt-2 max-h-[60vh] space-y-2 overflow-y-scroll overscroll-contain pr-1"
                style={{ scrollbarGutter: 'stable' }}
              >
                {displayedProducts.length ? (
                  displayedProducts.map((product) => (
                    <div
                      key={product.key}
                      className="flex items-center gap-3 rounded-lg border border-gray-100 p-2"
                    >
                      {product.image ? (
                        <img
                          src={product.image}
                          alt=""
                          className="h-12 w-12 rounded-md object-cover"
                        />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-gray-100">
                          <ImagePlus size={18} />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-base font-black text-gray-900 sm:text-lg">
                          {product.productName}
                        </p>
                        <p className="truncate text-sm text-gray-500">
                          {product.brandName} | {product.quantityText || 'Qty'} | Stock {product.stock}
                        </p>
                        <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <span className="text-xl font-black text-green-700">{product.discountPercent}% OFF</span>
                          <span className="text-lg font-black text-orange-600">{money(product.salePrice)}</span>
                          <span className="inline text-sm font-semibold text-gray-500 line-through decoration-1">
                            MRP {money(product.mrp)}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => addFinancialAsSlide(product)}
                        className="shrink-0 rounded-lg border px-3 py-2 text-xs font-semibold"
                      >
                        Add
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="rounded-lg border border-dashed p-3 text-sm text-gray-500">
                    No products found for this search.
                  </div>
                )}
              </div>
            </div>
            ) : null}

            {hasSelectedProducts ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
                <h2 className="text-lg font-black text-gray-900">Complete your Campaign</h2>
                <p className="mt-1 text-sm text-gray-600">Name this campaign and choose an outlet from the catalogue.</p>
                <div className="mt-4 grid gap-3">
                  <label className="text-sm font-bold text-gray-700">Campaign name</label>
                  <input
                    value={form.name}
                    onChange={(event) => updateForm('name', event.target.value)}
                    className="h-11 rounded-lg border border-amber-200 bg-white px-3 text-base"
                    placeholder="Advertisement name"
                  />
                  <label className="text-sm font-bold text-gray-700">Outlet</label>
                  <select
                    value={form.outlet_id}
                    onChange={(event) => updateForm('outlet_id', event.target.value)}
                    className="h-11 rounded-lg border border-amber-200 bg-white px-3 text-base"
                  >
                    <option value="">All outlets</option>
                    {outlets.map((outlet) => (
                      <option key={getOutletId(outlet)} value={getOutletId(outlet)}>
                        {getOutletLabel(outlet)}
                      </option>
                    ))}
                  </select>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <input
                      type="date"
                      value={form.start_date}
                      onChange={(event) => updateForm('start_date', event.target.value)}
                      className="h-11 rounded-lg border border-amber-200 bg-white px-3 text-base"
                      aria-label="Campaign start date"
                    />
                    <input
                      type="date"
                      value={form.end_date}
                      onChange={(event) => updateForm('end_date', event.target.value)}
                      className="h-11 rounded-lg border border-amber-200 bg-white px-3 text-base"
                      aria-label="Campaign end date"
                    />
                  </div>
                </div>
              </div>
            ) : null}

          </div>

          <div className="space-y-4">
            {editingAdId || activeEditor === 'featured-products' ? (
            <section id="featured-products" className="scroll-mt-4 rounded-2xl border border-emerald-200 bg-gradient-to-br from-white to-emerald-50 p-4 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <span className="text-xs font-black uppercase tracking-wide text-emerald-700">3 · Two featured products</span>
                  <h2 className="mt-1 text-lg font-black text-gray-900">Offers displayed beside the center video</h2>
                  <p className="text-sm text-gray-600">Hadavidi uses the two products with the highest discount. Review them here before publishing.</p>
                </div>
                <button
                  type="button"
                  onClick={useFeaturedSideOffers}
                  disabled={!featuredSideProducts.length}
                  className="h-10 shrink-0 rounded-lg bg-emerald-700 px-4 text-sm font-bold text-white hover:bg-emerald-800 disabled:bg-gray-300"
                >
                  Use these two offers
                </button>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {[0, 1].map((index) => {
                  const product = featuredSideProducts[index];
                  return (
                    <article key={product?.key || index} className="flex min-h-28 items-center gap-3 rounded-xl border border-emerald-100 bg-white p-3 shadow-sm">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-emerald-700 text-sm font-black text-white">{index + 1}</span>
                      {product?.image ? (
                        <img src={product.image} alt="" className="h-20 w-20 shrink-0 rounded-lg bg-amber-50 object-contain p-1" />
                      ) : (
                        <div className="grid h-20 w-20 shrink-0 place-items-center rounded-lg bg-gray-100"><ImagePlus size={22} /></div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-lg font-black text-gray-900">{product?.productName || 'Offer syncing'}</p>
                        {product ? (
                          <>
                            <p className="truncate text-sm font-semibold text-gray-500">{product.brandName} | {product.quantityText || 'Qty'}</p>
                            <p className="mt-1 text-lg font-black text-orange-600">{money(product.salePrice)}</p>
                            <div className="flex flex-wrap items-baseline gap-2">
                              <span className="inline text-sm font-semibold text-gray-500 line-through decoration-1">MRP {money(product.mrp)}</span>
                              <span className="text-xl font-black text-emerald-700">{product.discountPercent}% OFF</span>
                            </div>
                          </>
                        ) : <p className="text-xs text-gray-500">Add discounted products to continue.</p>}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
            ) : null}

            {editingAdId || activeEditor === 'center-video' ? (
            <section id="center-video" className="scroll-mt-4 rounded-2xl border border-blue-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <span className="text-xs font-black uppercase tracking-wide text-blue-700">2 · Center video</span>
                    <h2 className="font-bold text-gray-900">Center Video</h2>
                    <p className="text-xs text-gray-500">Video is optional. Without it, Hadavidi shows three best offers.</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${hasTvVideo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {hasTvVideo ? 'Video ready' : 'Offers only'}
                  </span>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 sm:p-4">
                    <div className="flex items-center gap-2 text-blue-950">
                      <Video size={18} />
                      <p className="text-sm font-black">Optional center video</p>
                    </div>
                    <p className="mt-1 text-xs text-blue-800">Paste a public YouTube link, or upload an MP4/WebM video.</p>
                    <label className="mt-3 block text-xs font-bold text-blue-950">YouTube or video URL</label>
                    <div className="relative mt-1">
                      <Link2 className="pointer-events-none absolute left-3 top-3 text-blue-500" size={16} />
                    <input
                      value={tvScreenUrl}
                      onChange={(event) => setTvScreenUrl(event.target.value)}
                        className="h-11 w-full rounded-lg border border-blue-200 bg-white pl-9 pr-3 text-sm"
                        placeholder="https://youtube.com/watch?v=..."
                    />
                    </div>
                    <div className="my-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-blue-400">
                      <span className="h-px flex-1 bg-blue-200" /> or upload <span className="h-px flex-1 bg-blue-200" />
                    </div>
                    <label className="inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-blue-200 bg-white text-sm font-bold text-blue-700 hover:bg-blue-100">
                      <Upload size={16} />
                      Choose video file
                      <input
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={(event) => handleTvScreenFile(event.target.files?.[0])}
                      />
                    </label>
                    {hasTvVideo ? (
                      <div className="mt-3 flex items-start gap-2 rounded-lg bg-green-100 p-2 text-xs font-semibold text-green-800">
                        <CheckCircle2 className="mt-0.5 shrink-0" size={15} />
                        Video selected. Click “Publish to Hadavidi” to make it live.
                      </div>
                    ) : null}
                  </div>
                </div>
            </section>
            ) : null}

            <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="border-b px-4 py-3">
                <h2 className="font-bold text-gray-900">Published Playlists</h2>
              </div>
              <div className="max-h-72 overflow-auto divide-y divide-gray-100">
                {loading ? (
                  <div className="p-4 text-sm text-gray-500">Loading...</div>
                ) : items.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">No advertisements yet.</div>
                ) : (
                  items.map((item) => (
                    <div key={item.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <p className="truncate font-bold text-gray-900">{item.name}</p>
                        <p className="text-xs text-gray-500">
                          {item.template} | {item.status} | {item.details?.length || 0} slide(s)
                        </p>
                        {isAdvertisementExpired(item) ? (
                          <p className="mt-1 text-xs font-bold text-amber-700">
                            Expired {String(item.end_date).slice(0, 10)} — not currently visible on Hadavidi
                          </p>
                        ) : null}
                      </div>
                      <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end">
                        <button
                          type="button"
                          onClick={() => editAdvertisement(item)}
                          className="flex-1 rounded-lg border px-3 py-2 text-sm font-semibold sm:flex-none"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleAdvertisementStatus(item)}
                          className="flex-1 rounded-lg border px-3 py-2 text-sm font-semibold sm:flex-none"
                        >
                          {item.status === 'active' && !isAdvertisementExpired(item)
                            ? 'Unpublish'
                            : isAdvertisementExpired(item)
                              ? 'Republish'
                              : 'Publish'}
                        </button>
                        <button
                          type="button"
                          onClick={() => dispatch(deleteAdvertisement(item.id))}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-600 hover:bg-red-50"
                          aria-label="Delete advertisement"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        </section>
      </main>
    </StockManagerLayout>
  );
};

export default AdvertisementsPage;
